/**
 * Admin → KITE Pages list. Shows every page for the tenant with status
 * (Draft / Published), public URL when live, and quick Edit / Delete /
 * View public actions.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { KitePage } from '@xsc/shared';
import { api } from '../lib/api-client.js';

interface ListResp { data: KitePage[] }

export function AdminKitePagesPage(): JSX.Element {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['kite-pages'],
    queryFn: async (): Promise<ListResp> =>
      (await api.get('/v1/admin/kite-pages')) as ListResp,
  });

  const create = useMutation({
    mutationFn: async (input: { slug: string; title: string }): Promise<KitePage> =>
      (await api.post('/v1/admin/kite-pages', input)) as KitePage,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kite-pages'] }),
  });
  const del = useMutation({
    mutationFn: async (id: string) => { await api.delete(`/v1/admin/kite-pages/${id}`); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['kite-pages'] }),
  });

  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">KITE Pages</h1>
          <p className="text-sm text-ink-muted">
            Drag-and-drop landing pages. Drafts are private; publishing makes a page available at
            <code className="mx-1">/k/&lt;slug&gt;</code>.
          </p>
        </div>
      </header>

      <section className="border border-border rounded-md p-4 space-y-3 bg-muted/30">
        <h2 className="text-base font-medium">Create a new page</h2>
        <form
          className="grid sm:grid-cols-3 gap-3 items-end"
          onSubmit={(e) => {
            e.preventDefault();
            if (!slug || !title) return;
            create.mutate({ slug, title }, {
              onSuccess: () => { setSlug(''); setTitle(''); },
            });
          }}
        >
          <label className="text-sm">
            <div className="text-ink-muted text-xs mb-1">Title</div>
            <input
              className="w-full rounded border border-border bg-bg px-2 py-1.5"
              value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Spring 2026 Open House"
            />
          </label>
          <label className="text-sm">
            <div className="text-ink-muted text-xs mb-1">Slug</div>
            <input
              className="w-full rounded border border-border bg-bg px-2 py-1.5"
              value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase())}
              pattern="[a-z0-9][a-z0-9-]{0,59}"
              placeholder="spring-open-house"
            />
          </label>
          <button
            type="submit"
            disabled={create.isPending || !slug || !title}
            className="rounded bg-accent text-white px-3 py-2 disabled:opacity-50"
          >
            {create.isPending ? 'Creating…' : 'Create draft'}
          </button>
        </form>
        {create.isError && (
          <p className="text-sm text-red-500">{(create.error as Error).message}</p>
        )}
      </section>

      <section>
        {list.isLoading && <p className="text-sm text-ink-muted">Loading…</p>}
        {list.isError && <p className="text-sm text-red-500">Failed to load pages.</p>}
        {list.data && list.data.data.length === 0 && (
          <p className="text-sm text-ink-muted">No pages yet. Create your first above.</p>
        )}
        {list.data && list.data.data.length > 0 && (
          <div className="border border-border rounded-md overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Slug</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Version</th>
                  <th className="px-3 py-2">Updated</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {list.data.data.map((p) => (
                  <tr key={p.id} className="border-t border-border">
                    <td className="px-3 py-2 font-medium">{p.title}</td>
                    <td className="px-3 py-2 font-mono text-xs">{p.slug}</td>
                    <td className="px-3 py-2">
                      <span className={
                        p.status === 'published'
                          ? 'inline-block px-2 py-0.5 text-xs rounded bg-green-100 text-green-800'
                          : 'inline-block px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-800'
                      }>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-muted">
                      draft v{p.draft_version}
                      {p.published_version != null && ` · pub v${p.published_version}`}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-muted">
                      {new Date(p.updated_at).toLocaleString()}
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <Link to={`/admin/kite-pages/${p.id}`} className="text-accent underline mr-3">Edit</Link>
                      {p.status === 'published' && (
                        <a href={`/k/${p.published_slug ?? p.slug}`} target="_blank" rel="noreferrer"
                           className="text-accent underline mr-3">View</a>
                      )}
                      <button
                        type="button"
                        className="text-red-500 underline"
                        onClick={() => { if (confirm(`Delete "${p.title}"?`)) del.mutate(p.id); }}
                      >Delete</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
